import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiClock, FiPackage, FiTruck, FiDollarSign, FiStar, FiChevronRight, FiMessageCircle, FiList } from 'react-icons/fi';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor } from '../../utils/formatters';

const OrderCard = ({ order, onClick, showSecurityCode = false, actions, showReviewButton = false, showChatButton = false, showItemDetails = false }) => {
  const navigate = useNavigate();
  
  // Status progress indicator
  const getStatusProgress = () => {
    const statuses = ['pendente', 'pago', 'em_preparo', 'pronto', 'em_entrega', 'entregue'];
    const currentIndex = statuses.indexOf(order.status);
    return ((currentIndex + 1) / statuses.length) * 100;
  };
  
  return (
    <div
      className="bg-white rounded-2xl shadow-md overflow-hidden card-hover cursor-pointer animate-fadeIn"
      onClick={onClick}
      data-testid={`order-card-${order.id}`}
    >
      {/* Status Progress Bar */}
      <div className="h-1 bg-gray-100">
        <div 
          className={`h-full transition-all duration-500 ${
            order.status === 'entregue' ? 'bg-green-500' : 
            order.status === 'cancelado' ? 'bg-red-500' : 'bg-[#DC143C]'
          }`}
          style={{ width: `${getStatusProgress()}%` }}
        />
      </div>
      
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">#{order.id.slice(0, 8)}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <h3 className="font-semibold text-gray-800 mt-1">{order.store_name}</h3>
          </div>
          <FiChevronRight className="text-gray-400" />
        </div>
        
        {/* NOVO: Detalhes dos Itens para Lojista */}
        {showItemDetails && order.items && order.items.length > 0 && (
          <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FiList size={14} /> Itens do Pedido
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {order.items.map((item, index) => (
                <div key={index} className="p-3 flex gap-3" data-testid={`order-item-${index}`}>
                  {/* Foto do produto */}
                  {item.photos && item.photos[0] && (
                    <img 
                      src={item.photos[0]} 
                      alt={item.product_name}
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{item.product_name}</p>
                    
                    {/* Descrição resumida */}
                    {item.product_description && (
                      <p className="text-xs text-gray-500 line-clamp-1">{item.product_description}</p>
                    )}
                    
                    {/* Variações selecionadas */}
                    <div className="flex flex-wrap gap-2 mt-1">
                      {item.selected_color && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          🎨 {item.selected_color}
                        </span>
                      )}
                      {item.selected_size && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          📏 Tam: {item.selected_size}
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Qtd: {item.quantity}
                      </span>
                    </div>
                    
                    <p className="text-sm font-semibold text-[#DC143C] mt-1">
                      {formatCurrency(item.subtotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Info Grid - Versão simplificada quando não mostra detalhes */}
        {!showItemDetails && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <FiClock className="text-gray-500" size={14} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Data</p>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <FiPackage className="text-gray-500" size={14} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Itens</p>
                <p className="font-medium">{order.items.length} {order.items.length === 1 ? 'produto' : 'produtos'}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Address */}
        <div className="flex items-start gap-2 mt-3 p-2 bg-gray-50 rounded-lg">
          <FiMapPin className="text-[#DC143C] mt-0.5 flex-shrink-0" size={14} />
          <p className="text-sm text-gray-600 line-clamp-1">{order.delivery_address}</p>
        </div>

        {/* Cliente info */}
        {showItemDetails && order.customer_name && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800">👤 {order.customer_name}</p>
            {order.customer_phone && (
              <p className="text-xs text-blue-600 mt-1">📞 {order.customer_phone}</p>
            )}
          </div>
        )}
        
        {/* Security Code */}
        {showSecurityCode && order.status !== 'entregue' && order.status !== 'cancelado' && (
          <div className="flex items-center gap-2 mt-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
            <FiTruck className="text-amber-600" />
            <div>
              <p className="text-xs text-amber-600">Código de Entrega</p>
              <p className="font-bold text-amber-800 text-lg tracking-wider">{order.security_code}</p>
            </div>
          </div>
        )}
        
        {/* Footer - Price */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-400">Total do pedido</p>
            <p className="text-xl font-bold text-[#DC143C]">{formatCurrency(order.total_value)}</p>
          </div>
          
          {/* Review Button */}
          {showReviewButton && order.status === 'entregue' && !order.reviewed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/cliente/avaliar/${order.id}`);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-amber-400 text-gray-900 rounded-full font-medium text-sm hover:from-yellow-500 hover:to-amber-500 transition-all shadow-sm"
              data-testid={`review-order-${order.id}`}
            >
              <FiStar className="fill-current" /> Avaliar
            </button>
          )}
          
          {order.status === 'entregue' && order.reviewed && (
            <div className="flex items-center gap-1 text-green-600">
              <FiStar className="fill-current" />
              <span className="text-sm font-medium">Avaliado</span>
            </div>
          )}
          
          {/* Chat Button */}
          {showChatButton && order.status !== 'pendente' && order.status !== 'cancelado' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/cliente/pedido/${order.id}`);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-600 rounded-full font-medium text-sm hover:bg-blue-200 transition-all"
            >
              <FiMessageCircle /> Chat
            </button>
          )}
        </div>
        
        {actions && (
          <div className="mt-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCard;
